import { mockDoctors } from "./mockData";

export const GAME_TYPES = {
  scan: {
    name: "Specimen Scan",
    time: 30,
    badge: "bg-cyan-950 text-cyan-300 border-cyan-700/60",
    howTo: "Hold and drag across the contaminated case file to wipe the spore bloom away. Clear 70% of it to read the symptom.",
  },
  riddle: {
    name: "Riddle",
    time: 45,
    badge: "bg-purple-950 text-purple-300 border-purple-700/60",
    howTo: "This scientist only speaks in riddles. Pick the symptom the riddle describes. A wrong answer costs 10 seconds and a hint costs 5.",
  },
  purge: {
    name: "Spore Purge",
    time: 25,
    badge: "bg-emerald-950 text-emerald-300 border-emerald-700/60",
    howTo: "Spores are erupting from the sample tray. Burn 12 of them. Hitting a clean blue sample costs 3 seconds.",
  },
  memory: {
    name: "Sample Match",
    time: 45,
    badge: "bg-amber-950 text-amber-300 border-amber-700/60",
    howTo: "Flip the sealed vials two at a time and match all six specimen pairs to unseal the file.",
  },
  decode: {
    name: "Decrypt",
    time: 60,
    badge: "bg-rose-950 text-rose-300 border-rose-700/60",
    howTo: "The symptom name has been scrambled. Tap the letter tiles in the right order to rebuild it. Wrong letters cost 2 seconds.",
  },
  signal: {
    name: "Signal Lock",
    time: 45,
    badge: "bg-blue-950 text-blue-300 border-blue-700/60",
    howTo: "Watch the containment lights flash, then repeat the pattern. Survive three rounds. A wrong light costs 4 seconds.",
  },
  vitals: {
    name: "Vitals Lock",
    time: 30,
    badge: "bg-red-950 text-red-300 border-red-700/60",
    howTo: "Stop the sweeping needle inside the green band three times (tap LOCK or press Space). Misses cost 3 seconds.",
  },
};

export const CASES = {
  "siti-nurhaliza": {
    no: 1, symptom: "Clear Eyes", category: "Ocular", game: "scan", infected: true, survival: "72 HRS",
    clues: ["The whites are too white. No visible vessels at all.", "Patient hasn't blinked in eleven minutes and feels fine.", "It's in the eyes, and nothing looks wrong. That's the problem."],
    finding: "Spore filaments have bleached every capillary in the sclera. The eyes look perfect because there is no blood left in them.",
  },
  "amara-singh": {
    no: 2, symptom: "Dry Eyes", category: "Ocular", game: "riddle", infected: true, survival: "48 HRS",
    riddle: ["Two windows of mine once ran with rain,", "now there are no tears, just sand and pain.", "Blink all you like, the wells have run dry.", "What am I?"],
    clues: ["The tear ducts have stopped producing anything.", "Patient says it feels like sand behind the lids.", "It's an eye complaint, and all the moisture is gone."],
    finding: "The spore drinks the tear film first. Corneas crack within a day and the colony moves in through the fissures.",
  },
  "timur-bekmuratov": {
    no: 3, symptom: "Eye Bleeding", category: "Ocular", game: "purge", infected: true, survival: "6 HRS",
    clues: ["Red tracks running down both cheeks.", "The vessels in the sclera have ruptured.", "The eyes are weeping, but it isn't tears."],
    finding: "Haemorrhage from both orbits. The spore has reached the optic vessels. A cranial bleed follows.",
  },
  "callum-fraser": {
    no: 4, symptom: "Tired Eyes", category: "Ocular", game: "memory", infected: false,
    clues: ["Heavy lids, dark rings, constant rubbing.", "Patient has pulled three night shifts in the reactor wing.", "The eyes simply look exhausted."],
    finding: "Sleep debt, nothing more. The swab came back clean with no spore filaments in the tear film. Send them to bed.",
  },
  "lina-orantes": {
    no: 5, symptom: "Normal Skin", category: "Ocular", game: "decode", infected: false,
    clues: ["No marks, no lesions, no discolouration at all.", "The surface is warm, even and unremarkable.", "Nothing on the outside. Absolutely nothing."],
    finding: "Biopsy shows healthy tissue all the way down. Sometimes normal skin is just normal skin.",
  },
  "james-harrington": {
    no: 6, symptom: "Sunburn", category: "Dermatology", game: "vitals", infected: true, survival: "3 DAYS",
    clues: ["Red, hot, peeling skin.", "The patient hasn't been above ground in three years.", "It looks like far too long in the sun."],
    finding: "There is no sun in the bunker. The burn is the spore digesting the epidermis from within.",
  },
  "amaka-ezeobi": {
    no: 7, symptom: "Freckles", category: "Dermatology", game: "signal", infected: false,
    clues: ["Small brown flecks across the nose and cheeks.", "The patient has had them since childhood.", "They look like ordinary sun spots."],
    finding: "Old pigment, unchanged in years. No spore clusters under the scope.",
  },
  "eleanor-whitaker": {
    no: 8, symptom: "Dry Skin", category: "Dermatology", game: "scan", infected: false,
    clues: ["Flaking, tight, cracked patches.", "The whole sector has been on recycled air for weeks.", "The skin is losing its moisture."],
    finding: "Low humidity from the air scrubbers. Every worker in the sector has it. No fungal growth.",
  },
  "mei-ling-chen": {
    no: 9, symptom: "Minor Cuts", category: "Dermatology", game: "riddle", infected: true, survival: "36 HRS",
    riddle: ["Thin red lines that no blade has made,", "I will not close and I will not fade.", "So small you'd shrug and look away.", "What marks the patient's arms today?"],
    clues: ["Tiny nicks along both forearms.", "They refuse to scab over.", "Small wounds the patient can't remember getting."],
    finding: "The cuts are exit points. Spores are breaking out of the skin, not getting in.",
  },
  "rajiv-malhotra": {
    no: 10, symptom: "Skin Rash", category: "Dermatology", game: "purge", infected: false,
    clues: ["Raised red welts across the forearms.", "It started the day the new decontamination soap arrived.", "Itchy, blotchy, inflamed skin."],
    finding: "A contact allergy to the new decon soap. It looks terrible, but there is no mycelium in it.",
  },
  "sofia-alvarez": {
    no: 11, symptom: "Bruises", category: "Dermatology", game: "memory", infected: true, survival: "30 HRS",
    clues: ["Purple and yellow patches with no injury reported.", "They appear overnight.", "Marks as if the patient was struck, but they weren't."],
    finding: "Subdermal bleeding as the colony ruptures capillaries. The marks spread outward in rings.",
  },
  "hannah-oconnell": {
    no: 12, symptom: "Necrosis", category: "Dermatology", game: "decode", infected: true, survival: "12 HRS",
    clues: ["Black tissue at the fingertips.", "No feeling at all in the affected area.", "The flesh is dying and turning dark."],
    finding: "Tissue death. The spore has finished feeding here and is moving inward. Amputation will not save them.",
  },
  "marcus-wright": {
    no: 14, symptom: "Normal Breathing", category: "Respiratory", game: "vitals", infected: false,
    clues: ["Steady rise and fall of the chest.", "Oxygen saturation perfectly normal.", "Every breath sounds textbook."],
    finding: "Lungs are clear on every scan and the airway sample is sterile. They are simply breathing normally.",
  },
  "david-mccallum": {
    no: 15, symptom: "Light Snoring", category: "Respiratory", game: "signal", infected: false,
    clues: ["A soft rattle while they sleep.", "Their bunk-mate says they've snored for years.", "A quiet noise at night. Nothing alarming."],
    finding: "A deviated septum and an old habit. The airway swab is clean.",
  },
  "priya-patel": {
    no: 16, symptom: "Seasonal Sniffles", category: "Respiratory", game: "riddle", infected: true, survival: "4 DAYS",
    riddle: ["I arrive with the spring and I leave with the fall,", "but down in the bunker there's no season at all.", "A drip and a sneeze, so harmless I seem.", "What am I?"],
    clues: ["Runny nose, mild sneezing.", "There are no seasons underground.", "It looks just like hay fever."],
    finding: "The sinus membranes are shedding spores with every sneeze. The patient is the outbreak now.",
  },
  "robert-jenkins": {
    no: 17, symptom: "Hiccuping", category: "Respiratory", game: "riddle", infected: false,
    riddle: ["I jump in your chest with a squeak in your throat,", "I interrupt every sentence and note.", "Hold your breath, sip water, I'll still say hello.", "What am I?"],
    clues: ["Sudden spasms of the diaphragm.", "They started right after a rushed canteen meal.", "A rhythmic 'hic' that comes and goes."],
    finding: "Swallowed air and fizzy ration water. The diaphragm is irritated, not infected.",
  },
  "sarah-thompson": {
    no: 18, symptom: "Persistent Cough", category: "Respiratory", game: "scan", infected: false,
    clues: ["A dry hack that won't clear.", "The patient has been drilling in the new east tunnel.", "Coughing from the chest that keeps coming back."],
    finding: "Rock dust from the tunnel works. The sputum sample shows grit, not spores. Issue them a better mask.",
  },
  "michael-davies": {
    no: 19, symptom: "Bloody Cough", category: "Respiratory", game: "memory", infected: true, survival: "8 HRS",
    clues: ["Red flecks in the tissue.", "The lungs are bleeding.", "Coughing, and something red comes up."],
    finding: "Haemoptysis. The colony has broken through the lung wall. Isolation is now the only protocol.",
  },
  "elizabeth-harper": {
    no: 20, symptom: "Normal Temperature", category: "Vitals", game: "vitals", infected: true, survival: "6 DAYS",
    clues: ["The thermometer reads exactly 37.0°C.", "Every reading, every hour, identical.", "No fever, no chill. Body heat is ordinary."],
    finding: "The body has stopped fighting. No fever means the immune system has already surrendered to the spore.",
  },
  "andrew-mitchell": {
    no: 21, symptom: "Normal Pulse", category: "Vitals", game: "signal", infected: false,
    clues: ["72 beats per minute, perfectly regular.", "It rises with exercise and settles at rest, as it should.", "The heart rate is completely average."],
    finding: "A healthy heart responding exactly as a heart should. No colony rhythm.",
  },
  "claire-watson": {
    no: 22, symptom: "Low Resting Pulse", category: "Vitals", game: "decode", infected: false,
    clues: ["A heart rate of 42 at rest.", "The patient ran the bunker's endurance squad for ten years.", "The heartbeat is slow and steady."],
    finding: "An athlete's heart. Strong, slow and spore-free.",
  },
  "thomas-brown": {
    no: 23, symptom: "Elevated Temperature", category: "Vitals", game: "riddle", infected: false,
    riddle: ["I climb without legs and I rise without stairs,", "I flush up the cheeks and I dampen the hairs.", "Not yet a blaze, but more than just warm.", "What am I?"],
    clues: ["Readings sitting around 38.2°C.", "Half the dormitory has the same sniffly bug.", "The body is running hot."],
    finding: "A common winter virus doing the rounds. The immune system is winning this one. No spore markers.",
  },
  "victoria-scott": {
    no: 24, symptom: "Critical Fever", category: "Vitals", game: "purge", infected: true, survival: "2 HRS",
    clues: ["The thermometer reads 41.8°C.", "The patient is delirious and convulsing.", "The heat is now life-threatening."],
    finding: "Terminal hyperthermia. The colony is cooking its host to release spores. Seal the room.",
  },
  "benjamin-clark": {
    no: 25, symptom: "Fresh Breath", category: "Oral / Breath", game: "scan", infected: true, survival: "6 DAYS",
    clues: ["The breath smells clean and minty.", "The patient hasn't brushed in days.", "Nothing unpleasant on the breath at all."],
    finding: "The spore masks itself. A clean, sweet breath means the mouth has been sterilised by the colony.",
  },
  "lucy-adams": {
    no: 26, symptom: "Coffee Breath", category: "Oral / Breath", game: "decode", infected: false,
    clues: ["A bitter, roasted odour.", "A trader smuggled a crate of coffee down last week.", "It smells like a morning brew."],
    finding: "It's actual coffee. Contraband, but harmless.",
  },
  "daniel-hughes": {
    no: 27, symptom: "Garlic Breath", category: "Oral / Breath", game: "riddle", infected: false,
    riddle: ["I keep vampires away and I cling to your tongue,", "I'm cloves on the plate and a cloud from the lung.", "Your colleagues step back whenever you talk.", "What am I?"],
    clues: ["A pungent, sulphurous odour.", "Tonight's canteen ration was garlic stew.", "It smells like a strong kitchen ingredient."],
    finding: "Garlic stew. The whole canteen smells like it.",
  },
  "rachel-morgan": {
    no: 28, symptom: "Morning Breath", category: "Oral / Breath", game: "riddle", infected: false,
    riddle: ["I'm born in the night while you lie in your bed,", "I greet you at dawn from your mouth, not your head.", "A brush and a breakfast will send me away.", "What am I?"],
    clues: ["A stale odour on waking.", "It's gone after brushing and breakfast.", "It smells like the patient just woke up."],
    finding: "Ordinary overnight breath, gone by breakfast.",
  },
  "jonathan-taylor": {
    no: 29, symptom: "Death-like Breath", category: "Oral / Breath", game: "riddle", infected: true, survival: "1 HR",
    riddle: ["I leave with each word but I smell of the grave,", "of meat left to rot in a damp, sealed cave.", "The living exhale me, though I belong to the dead.", "What am I?"],
    clues: ["The odour of rotting meat.", "Staff refuse to enter the room.", "The breath smells like a corpse."],
    finding: "Decomposition while still breathing. The lungs are already dead tissue. There is nothing left to save.",
  },
  "helen-edwards": {
    no: 30, symptom: "Alert Behavior", category: "Behavioural", game: "vitals", infected: true, survival: "3 DAYS",
    clues: ["Wide-eyed, hyper-focused, tracking every sound.", "Hasn't slept in four days and doesn't feel tired.", "The patient is very awake and very aware."],
    finding: "The spore floods the brain with stimulants so the host stays on its feet while it spreads.",
  },
  "peter-wilson": {
    no: 31, symptom: "Tired Behavior", category: "Behavioural", game: "riddle", infected: false,
    riddle: ["I yawn as I speak and I nod as I stand,", "my eyelids are anchors, my legs made of sand.", "Two shifts in a row and I'm out on my feet.", "What am I?"],
    clues: ["Slumped, slow, yawning mid-sentence.", "They've been covering two shifts since the outbreak began.", "No energy at all."],
    finding: "Pure exhaustion from double shifts. Bloods are clean. Give them a day off.",
  },
  "anna-roberts": {
    no: 32, symptom: "Anxious Behavior", category: "Behavioural", game: "riddle", infected: true, survival: "48 HRS",
    riddle: ["I pace without going, I shake without cold,", "I ask 'Am I safe?' a hundred times told.", "I swear something's growing where nothing should be.", "What am I?"],
    clues: ["Pacing, shaking, constant reassurance-seeking.", "Says something is 'growing' inside them.", "Nervous and panicked. They can't settle."],
    finding: "They're right. The fear is the brain detecting the mycelium reaching the amygdala.",
  },
};

export const ROSTER = [...mockDoctors].sort((a, b) => CASES[a.id].no - CASES[b.id].no);

export const ALL_SYMPTOMS = Object.values(CASES).map((c) => ({ symptom: c.symptom, category: c.category }));

export const fileNo = (entry) => `SP-${String(entry.no).padStart(3, "0")}`;

export const SEALED_NO = "SP-▒▒▒";

export const verdictLabel = (entry) => (entry.infected ? "INFECTED" : "NOT INFECTED");

export const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};
